import { FC, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  Box, Typography, Grid, Paper, Divider, 
  FormControl,
  RadioGroup,
  Radio
} from "@mui/material"

import { default as leadsService } from "../../../services/leads";
import { default as cupsService } from "../../../services/cups";

import { useForm, Controller, FormProvider, FieldErrors } from "react-hook-form"
import { z, ZodErrorMap, ZodIssueCode } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import AccessTariffSelectField from "../../customFields/AccessTariffSelectField";
import { cupsValid } from "../../../../form-fields/CUPSField";
import ComerOriginSelectField from "@/components/form-fields/ComerOriginSelect/ComerOriginSelectField";
import FileUploadField from "../../../../form-fields/FileUploadField";
import { useTranslation } from "react-i18next";
import { RootState } from "@/store";
import { FormControlLabel } from "@mui/material";
import { Checkbox } from "@mui/material";
import { FormHelperText } from "@mui/material";
import GasProductSelectField from "../../customFields/GasProductSelectField";
import useLangs from "@/languages/hooks/useLangs";
import { TextField } from "@mui/material";
import { IStepForm, StepDualGasContractFormData } from "../../../types/stepsFormData";
import { useSettingsContext } from "../../../../Settings/hooks/useSettingsContext";
import { SubmitButtonContext } from "../../../Contractation";
import * as electricityContractationActions from "../../../actions/electricityContractation";
import { useGasProducts } from "../../../hooks/data/useProducts";


const FormGasContract: FC<IStepForm<StepDualGasContractFormData>> = ({
  onSubmit
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const { currentLang } = useLangs();
  const dispatch = useDispatch();

  const contractation = useSelector((state: RootState) => state[`electricityContractation`]);

  const [comerOriginEnabled, setComerOriginEnabled] = useState(Settings?.contractation?.selectComerOrigin ?? false);

  const onAboutGasContractSubmit = async (values: StepDualGasContractFormData) => {

    // Doing this is necessary because there are fields that we need which are 
    // not controlled by react-hook-form or validated by zod which are not sent
    // otherwise. This ensures all the CUPS SIPS data is sent to the redux.

    if (values.includeGas) {
      values.cupsSIPSData = watchedCUPSSIPSData;
    } else {
      // This is needed because ZOD only validates includesGas and 
      // the default values are removed, causing validation errors.
      values = {
        ...contractation.steps.dualGasContract,
        ...values
      }
    }

    await onSubmit(values);
  };

  const handleNoAvailableComersUpdate = () => {
    setComerOriginEnabled(false);
  };

  const customErrorMap: ZodErrorMap = (issue, ctx) => {
    if (issue.code === ZodIssueCode.invalid_enum_value) {
      return { message: t('common:text.required_field') };
    }
    if (issue.code === ZodIssueCode.invalid_type) {
      return { message: t('common:text.required_field') };
    }

    // Retorna el missatge per defecte per altres errors
    return { message: ctx.defaultError };
  };

  const schema = z.discriminatedUnion("includeGas", [
    z.object({includeGas: z.literal(false)}),
    z.object({
      includeGas: z.literal(true),
      activeContract: z.boolean({errorMap: customErrorMap}),
      changeOwner: z.boolean().nullable().refine(val => {
        if (watchedActiveContract) {
          return typeof(val) === "boolean"
        } else {
          return true
        }
      }, {message: t('common:text.required_field')}),
      comerOrigin: z.string().refine(val => {
        if (comerOriginEnabled) {
          return val
        } else {
          return true
        }
      }, {message: t('common:text.required_field')}),
      cups: z.string().min(1, t('common:text.required_field'))
      .superRefine((cups, ctx) => {
        if (!cupsValid(cups)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('common:text.contractation_cups_not_valid')
          });
        }
      }),
      accessTariff: z.object({
        id: z.number().nonnegative({message: 'common:text.required_field'}),
        name: z.string().optional(),
      }),
      cupsSIPSData: z.object({
        cupsAvailable: z.boolean().refine(val => val, {
          message: t('common:text.contractation_cups_not_available')
        }),
      }),
      selectedProduct: z.number({
        invalid_type_error: t('common:text.required_field')
      }).positive({message: t('common:text.required_field')}),
      selectedProductName: z.string().optional(),
      
      invoice: z.object({
        name: z.string(),
        content: z.string().regex(/^data:.*\/[a-zA-Z]+;base64,/, t('common:text.invalid_attachment_type')),
      })
        .array()
    }) satisfies z.ZodType<Omit<Extract<StepDualGasContractFormData, {includeGas: true}>, "cupsSIPSData">>
  ])
  .superRefine((data, ctx) => {
    if (data.includeGas) {
      if ( 
        data.activeContract === true && 
        (Settings?.contractation?.invoiceRequired ?? true) &&
        ( !data.invoice || data.invoice?.length === 0 )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('common:text.required_field'),
          path: ["invoice"],
        });
      }
    }
  });

  const methods = useForm<StepDualGasContractFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      includeGas: Settings?.contractation?.dual?.isGasMandatory ||
        contractation.steps.dualGasContract.includeGas,
      ...contractation.steps.dualGasContract,
    }
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    register,
    trigger,
    formState: { errors, isSubmitting }
  } = methods;

  // RHF discriminated unions limitation 
  // https://stackoverflow.com/questions/74219465/typescript-react-hook-form-error-handling-with-zod-union-schema
  const fullErrors: FieldErrors<Extract<StepDualGasContractFormData, {includeGas: true}>> = errors;

  const watchedActiveContract = watch("activeContract");
  const watchedChangeOwner = watch("changeOwner");
  const watchedIncludeGas = watch("includeGas");
  const watchedVals = watch();
  const watchedCUPSSIPSData = watch("cupsSIPSData");
  const watchedSelectedProduct = watch("selectedProduct");
  const watchedCUPS = watch("cups");

  const productsData = useGasProducts({
    accessTariff: getValues("includeGas") ? getValues("accessTariff.id") : -1, 
    cups: contractation.steps.installation.cups, 
    lang: currentLang
  });

 useEffect(() => {
    if (watchedActiveContract === false) {
      setValue("changeOwner", null);
    }
  }, [watchedActiveContract]);

  useEffect(() => {
    if (watchedIncludeGas && cupsValid(watchedCUPS)) {
      leadsService.fetchGasContractationConfig(watchedCUPS).then(res => {
        dispatch(
          electricityContractationActions.contractationDualConfigurationReceive(res)
        );
      })
    }
  }, [watchedIncludeGas, watchedCUPS]);

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {handleSubmit(onAboutGasContractSubmit)()},
      loading: isSubmitting,
      // disabled: includeGas && !!Object.keys(errors).length
    });
  }, [isSubmitting]);

  useEffect(() => {
    if(productsData.isSuccess && productsData.data.length) {
      const currentSelectedProductId = productsData.data.find(prod => prod.id === getValues("selectedProduct"))?.id;
      if(currentSelectedProductId) {
        setValue("selectedProduct", currentSelectedProductId);
      } else {
        setValue("selectedProduct", -1);
      }
    }
  }, [productsData.data]);

  const cupsMethods = register("cups");

  const handleCUPSChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    cupsMethods.onChange(event);
    if (cupsValid(event.target.value)) {
      cupsService.validateGasCUPSAvailability(
        event.target.value
      )
      .then(data => {
        setValue("cupsSIPSData", data);
        if( data?.accessTariff ) {
          setValue("accessTariff", {
            id: data.accessTariff, name: data.accessTariffName
          });
        }
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onAboutGasContractSubmit)}>

        {!Settings?.contractation?.dual?.isGasMandatory && 
          <Paper variant="outlined" sx={{pl: 2, mb: 2}}>
            <Controller
              name={"includeGas"}
              control={control}
              render={({ field }) => <FormControlLabel
                control={<Checkbox
                  title="Current gas contract"
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event);
                    if (event.target.checked) {
                      if (!Array.isArray(getValues("invoice"))) {
                        setValue("invoice", []);
                      }
                      if (getValues("comerOrigin") === undefined) {
                        setValue("comerOrigin", '');
                      }
                      if (getValues("selectedProduct") === undefined) {
                        setValue("selectedProduct", -1);
                      }
                    }
                  }}
                  />} 
                label={
                  <Typography variant="h6" color="primary">
                    {t('common:text.contractation_include_gas')}
                  </Typography>
                } 
              />}
            />
          </Paper>
        }

        {watchedIncludeGas && (
          <>
            <Typography variant="h6" color="primary">
              {t('common:text.contractation_current_gas_contract_data')}
            </Typography>
            <Typography variant="body2" sx={{mb: 2}}>
              {t('common:text.contractacion_about_installation_info')}
            </Typography>
            <Box sx={{ml: {md: 5}, mr: {md: 5}}}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {t('common:text.contractation_cups_title')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={4}>

                <TextField
                  label={t('common:text.contractation_cups_gas')}
                  {...cupsMethods}
                  onChange={handleCUPSChange}
                  error={!!(fullErrors?.cups?.message) || !!(fullErrors?.cupsSIPSData?.cupsAvailable?.message)}
                  style={{ width: "100%" }}
                />
                {!!(fullErrors.cups?.message) &&
                  <FormHelperText error={true}>
                    {fullErrors.cups.message}
                  </FormHelperText>
                }
                {!!(fullErrors?.cupsSIPSData?.cupsAvailable) &&
                  <FormHelperText error={true}>
                    {fullErrors.cupsSIPSData.cupsAvailable.message}
                  </FormHelperText>
                }

              </Grid>
              <Grid item xs={12}>
                <div className="helper-text">{t('common:text.contractation_cups_gas_helper')}</div>
                <Divider style={{marginBottom: 10, marginTop: 10}}/>
              </Grid>
              {/* Leave this because it doesn't make sense to show the same
                  address as electricity and also have it be readOnly
                {!Object.keys(elecContractration.address) && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('common:text.contracts_direction')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <AddressForm readOnly />
                  </Grid>
                </>
              )} */}
              <Grid item xs={12} sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <Typography variant="h6">
                  {t('common:text.contractation_housing_consumption')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={!(contractation.steps.installation.residenceType === 'business') ? 6 : 12}>
                <Paper elevation={3} sx={{padding: 2}}>
                  <Typography variant="h6">
                    {t("common:text.contractation_current_active_gas_contract_question")}
                  </Typography>
                  <Controller
                    name={"activeContract"}
                    control={control}
                    render={({field, fieldState}) => (
                      <FormControl>
                        <RadioGroup
                          {...field}
                          onChange={(event) => field.onChange(JSON.parse(event.target.value))}
                          row
                          name="current-active-contract-radio-group"
                        >
                          <FormControlLabel value={true} control={<Radio/>} label={t("common:text.generic_yes")}/>
                          <FormControlLabel value={false} control={<Radio />} label={t("common:text.generic_no")}/>
                        </RadioGroup>
                        { !!(fieldState.error) &&
                          <FormHelperText error={true}>
                            {fieldState.error.message}
                          </FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                </Paper>
              </Grid>
              {watchedActiveContract && 
                <Grid item xs={12} sx={{mt: 1, mb: 1}}>
                  <Paper elevation={3} sx={{padding: 2}}>
                    <Typography variant="h6">
                      {(
                        (Settings?.contractation?.nameInChangeOwnerQuestion ?? false) && 
                        (!!contractation.gasConfig?.name || !!contractation.gasConfig?.surName1 || !!contractation.gasConfig?.surName2 )
                      ) ?
                        t("common:text.contractation_change_owner_question_named", 
                          {owner: contractation.gasConfig.name + " " + 
                            contractation.gasConfig.surName1 + " " + 
                            contractation.gasConfig.surName2
                          })
                        : t("common:text.contractation_change_owner_question")
                      }
                    </Typography>

                    <Controller
                      name={"changeOwner"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl>
                          <RadioGroup
                            {...field}
                            onChange={(event) => field.onChange(JSON.parse(event.target.value))}
                            row
                            name="current-active-contract-radio-group"
                          >
                            <FormControlLabel value={true} control={<Radio/>} label={t("common:text.generic_yes")}/>
                            <FormControlLabel value={false} control={<Radio />} label={t("common:text.generic_no")}/>
                          </RadioGroup>
                          { !!(fieldState.error) &&
                            <FormHelperText error={true}>
                              {fieldState.error.message}
                            </FormHelperText>
                          }
                        </FormControl>
                      )}
                    />
                  </Paper>
                  <Divider sx={{mb: 1, mt: 1}}/>
                </Grid>
              }
              {(!contractation.steps.dualGasContract.sipsData || 
                !(getValues("accessTariff.id") >= 0)) && 
                watchedCUPS &&
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('common:text.contract_title')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={8} md={6} mb={2}>
                    <AccessTariffSelectField
                      gas={true}
                      readOnly={!!(watchedCUPSSIPSData?.accessTariff)}
                      label={t('common:text.contractation_access_tariff')}
                    />
                  </Grid>

                </>
              }
              {comerOriginEnabled &&
                <Grid item xs={12} md={6} sx={{ mb: 3, mt: 3 }}>
                  <ComerOriginSelectField
                    onNoAvailableComersUpdate={handleNoAvailableComersUpdate}
                    gas={true}
                  />
                </Grid>
              }
              {(
                getValues("activeContract") === true && 
                (Settings?.contractation?.invoiceRequired ?? true)
              ) && 
                <Grid item xs={12} mb={3}>
                  <Divider sx={{mb: 2, mt: 2}}/>
                  <Typography variant="h6">
                    { t(`common:text.contractation.gas.last_invoice`)}
                  </Typography>
                  <Paper sx={{p: 2}} variant="outlined">
                    <Controller
                      name={"invoice"}
                      control={control}
                      render={({ field }) => <FileUploadField
                        min={1}
                        max={3}
                        label={
                          t(`common:text.contractation.gas.last_invoice`) + 
                          `${Settings?.contractation?.invoiceRequired ? "*" : ""}`
                        }
                        hint={t(`common:text.contractation.gas.last_invoice_helper`)}
                        anotherLabel={t(`common:text.contractation.gas.last_invoice_add`)}
                        removeLabel={t('common:text.remove')}
                        meta={{
                          touched: !!fullErrors.invoice,
                          error: Array.isArray(fullErrors?.invoice) && fullErrors.invoice.map(err => err.content.message),
                        }}
                        input={field}
                      />}
                    />
                    { !!(fullErrors.invoice) && !Array.isArray(fullErrors.invoice) &&
                      <FormHelperText error={true} >
                        {fullErrors.invoice.message}
                      </FormHelperText>
                    }
                  </Paper>
                </Grid>
              }
            </Box>

            {getValues("accessTariff.id") >= 0 &&
              <>
                <Typography variant="h6" color="primary">
                  {t('common:text.contractation_new_pricelist')}
                </Typography>
                <Typography variant="body2" sx={{mb: 2}}>
                  {t('common:text.contractation_current_pressure_warning')}
                </Typography>
                <Box sx={{ml:{md: 5}, mr:{md: 5}}}>
                  <Controller
                    name={"selectedProduct"}
                    control={control}
                    render={({ field }) => <GasProductSelectField
                      availableProducts={productsData.isSuccess ? productsData.data : []}
                      loading={productsData.isLoading}
                      input={{
                        ...field,
                        onChange: (value) => {
                          field.onChange(value);
                          setValue("selectedProductName", productsData.data?.find(
                            prod => (prod.id === value))?.name
                          )
                        }
                      }}
                    />}
                  />
                  {!!(fullErrors.selectedProduct) &&
                    <FormHelperText error={true}>
                      {fullErrors.selectedProduct.message}
                    </FormHelperText>
                  }
                </Box>
              </>
            }
          </>
        )}

      </form>
    </FormProvider>
  );
};

export default FormGasContract;
